

import React, { useState, useEffect } from "react";

export default function Hero3D() {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [textIndex, setTextIndex] = useState(0);

  const developerTexts = [
    "BLOCKCHAIN DEVELOPER",
    "WEB3 ARCHITECT", 
    "SMART CONTRACT ENGINEER",
    "DEFI SPECIALIST"
  ];

  useEffect(() => {
    setIsVisible(true);
    
    const interval = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % developerTexts.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setMousePosition({ x: x * 20, y: y * 20 });
  };

  return (
    <>
      {/* Developer-friendly fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600;700;800;900&family=Space+Grotesk:wght@300;400;500;600;700&display=swap"
        rel="stylesheet"
      />
      
      <div 
        className="w-full min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 overflow-hidden relative flex items-center"
        style={{fontFamily: 'Inter, sans-serif'}}
        onMouseMove={handleMouseMove}
      >
        {/* Animated Grid Background */}
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-500/10 to-transparent"
            style={{
              backgroundImage: `
                linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px',
              transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)`
            }}
          ></div>
        </div>

        {/* Floating Code Symbols */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {['{', '}', '</', '>', '()', '[]', '=>', '&&'].map((symbol, i) => (
            <div
              key={i}
              className="absolute text-cyan-500/20 font-mono text-2xl animate-pulse"
              style={{
                left: `${10 + (i * 12)}%`,
                top: `${20 + (i % 4) * 20}%`,
                animationDelay: `${i * 0.5}s`,
                transform: `translateY(${Math.sin(Date.now() * 0.001 + i) * 10}px) rotate(${i * 45}deg)`
              }}
            >
              {symbol}
            </div>
          ))}
        </div>

        {/* Geometric Shapes */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute top-1/4 left-1/4 w-32 h-32 border border-cyan-500 rotate-45 animate-spin-slow"
            style={{ transform: `translate(${mousePosition.x}px, ${mousePosition.y}px) rotate(45deg)` }}
          ></div>
          <div 
            className="absolute bottom-1/4 right-1/4 w-24 h-24 border border-purple-500 animate-pulse"
            style={{ transform: `translate(${-mousePosition.x * 0.5}px, ${-mousePosition.y * 0.5}px)` }}
          ></div>
          <div 
            className="absolute top-1/2 right-1/3 w-16 h-16 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-full animate-bounce"
            style={{ transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px)` }}
          ></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center min-h-screen">
            
            {/* Left Content */}
            <div className={`space-y-8 transform transition-all duration-2000 ${
              isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
            }`}>
              
              {/* Greeting */}
              <div className="overflow-hidden">
                <div className={`transform transition-all duration-1500 delay-300 ${
                  isVisible ? 'translate-y-0' : '-translate-y-full'
                }`}>
                  <p className="text-cyan-400 text-lg font-medium tracking-wider mb-2" style={{fontFamily: 'JetBrains Mono, monospace'}}>
                    console.log("Hello World!");
                  </p>
                </div>
              </div>

              {/* Main Name */}
              <div className="overflow-hidden">
                <div className={`transform transition-all duration-2000 delay-500 ${
                  isVisible ? 'translate-y-0' : 'translate-y-full'
                }`}>
                  <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-4">
                    <span 
                      className="inline-block hover:scale-105 transition-transform duration-300"
                      style={{
                        fontFamily: 'Space Grotesk, sans-serif',
                        textShadow: '0 0 30px rgba(6, 182, 212, 0.3)',
                        transform: `perspective(1000px) rotateX(${mousePosition.y * 0.1}deg) rotateY(${mousePosition.x * 0.1}deg)`
                      }}
                    >
                      MANEESH
                    </span>
                    <br />
                    <span 
                      className=" text-6xl inline-block text-transparent bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text hover:scale-105 transition-transform duration-300"
                      style={{
                        fontFamily: 'Space Grotesk, sans-serif',
                        transform: `perspective(1000px) rotateX(${mousePosition.y * 0.1}deg) rotateY(${mousePosition.x * 0.1}deg)`
                      }}
                    >
                      MADHUSUDHANAN
                    </span>
                  </h1>
                </div>
              </div>

              {/* Animated Role */}
              <div className="overflow-hidden h-20">
                <div className={`transform transition-all duration-2000 delay-700 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}>
                  <h2 
                    className="text-2xl md:text-4xl font-bold text-cyan-300 transition-all duration-1000"
                    style={{
                      fontFamily: 'JetBrains Mono, monospace',
                      textShadow: '0 0 20px rgba(6, 182, 212, 0.5)'
                    }}
                  >
                    {developerTexts[textIndex]}
                  </h2>
                </div>
              </div>

              {/* Description with Terminal Style */}
              <div className={`transform transition-all duration-2000 delay-1000 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
              }`}>
                <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6 hover:border-cyan-500/50 transition-colors duration-300">
                  <div className="flex items-center mb-4">
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <span className="ml-4 text-gray-400 text-sm" style={{fontFamily: 'JetBrains Mono, monospace'}}>
                      ~/developer/profile
                    </span>
                  </div>
                  <p 
                    className="text-gray-300 leading-relaxed text-lg"
                    style={{fontFamily: 'JetBrains Mono, monospace'}}
                  >
                    <span className="text-cyan-400">const</span>{' '}
                    <span className="text-yellow-400">developer</span>{' '}
                    <span className="text-white">=</span>{' '}
                    <span className="text-green-400">{`{`}</span>
                    <br />
                    &nbsp;&nbsp;<span className="text-purple-400">skills</span>: [<span className="text-orange-400">'Blockchain'</span>, <span className="text-orange-400">'Web3'</span>, <span className="text-orange-400">'Solidity'</span>],
                    <br />
                    &nbsp;&nbsp;<span className="text-purple-400">passion</span>: <span className="text-orange-400">'Decentralized Solutions'</span>,
                    <br />
                    &nbsp;&nbsp;<span className="text-purple-400">experience</span>: <span className="text-orange-400">'Enterprise Integration'</span>
                    <br />
                    <span className="text-green-400">{`}`}</span>;
                  </p>
                </div>
              </div>

              {/* Tech Stack Icons */}
              <div className={`transform transition-all duration-2000 delay-1200 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
              }`}>
                <div className="flex flex-wrap gap-4">
                  {['Ethereum', 'Solidity', 'React', 'Node.js', 'Docker', 'Git'].map((tech, i) => (
                    <div
                      key={tech}
                      className="px-4 py-2 bg-gray-800/50 backdrop-blur-sm border border-gray-600 rounded-full text-cyan-400 text-sm font-medium hover:border-cyan-500 hover:bg-cyan-500/10 transition-all duration-300 hover:scale-105"
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        animationDelay: `${i * 0.1}s`
                      }}
                    >
                      {tech}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Image Section */}
            <div className={`relative transform transition-all duration-2000 delay-800 ${
              isVisible ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-20 opacity-0 scale-90'
            }`}>
              <div className="relative group perspective-1000">
                
                {/* Holographic Effect Container */}
                <div 
                  className="relative"
                  style={{
                    transform: `perspective(1000px) rotateX(${mousePosition.y * 0.05}deg) rotateY(${mousePosition.x * 0.05}deg)`
                  }}
                >
                  {/* Animated Border */}
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 rounded-2xl blur-sm opacity-75 group-hover:opacity-100 animate-pulse"></div>
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 rounded-2xl animate-spin-slow opacity-30"></div>
                  
                  {/* Main Image Container */}
                  <div className="relative bg-black/20 backdrop-blur-sm rounded-2xl p-1 transform group-hover:scale-105 transition-all duration-700 hover:shadow-2xl hover:shadow-cyan-500/30">
                    <div className="overflow-hidden rounded-xl">
                      <img
                        src="/images/WhatsApp Image 2025-08-11 at 11.58.48 AM.jpeg"
                        alt="Maneesh Madhusudhanan - Blockchain Developer"
                        className="w-full h-auto max-w-md mx-auto object-cover transform group-hover:scale-110 transition-transform duration-1000 filter group-hover:brightness-110 group-hover:contrast-110"
                        loading="eager"
                      />
                      
                      {/* Holographic Overlays */}
                      <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1500 skew-x-12"></div>
                    </div>
                  </div>

                  {/* Floating Elements */}
                  <div className="absolute -top-6 -left-6 w-12 h-12 bg-cyan-500/80 rounded-full animate-bounce opacity-80 flex items-center justify-center text-white font-bold">
                    &lt;/&gt;
                  </div>
                  <div className="absolute -bottom-6 -right-6 w-10 h-10 bg-purple-500/80 rounded-full animate-pulse opacity-80 flex items-center justify-center text-white font-bold text-sm">
                    {}
                  </div>
                  <div className="absolute top-1/4 -left-8 w-6 h-6 bg-blue-500/80 rounded-full animate-ping opacity-60"></div>
                  <div className="absolute bottom-1/4 -right-8 w-8 h-8 border-2 border-cyan-400 rounded-full animate-spin opacity-60"></div>
                </div>

                {/* Particle Effects */}
                <div className="absolute inset-0 pointer-events-none">
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-1 h-1 bg-cyan-400 rounded-full animate-pulse"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animationDelay: `${i * 0.3}s`,
                        animationDuration: `${2 + Math.random() * 2}s`
                      }}
                    ></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Styles */}
        <style jsx>{`
          @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          
          @keyframes glow {
            0%, 100% { text-shadow: 0 0 20px rgba(6, 182, 212, 0.5); }
            50% { text-shadow: 0 0 30px rgba(6, 182, 212, 0.8), 0 0 40px rgba(6, 182, 212, 0.3); }
          }
          
          .animate-spin-slow {
            animation: spin-slow 10s linear infinite;
          }
          
          .animate-float {
            animation: float 4s ease-in-out infinite;
          }
          
          .animate-glow {
            animation: glow 2s ease-in-out infinite;
          }
          
          .perspective-1000 {
            perspective: 1000px;
          }

          /* Smooth text transitions */
          h2 {
            transition: all 0.5s ease-in-out;
          }
        `}</style>
      </div>
    </>
  );
}

// import React, { useState, useEffect } from "react";

// export default function Hero3D() {
//   const [isVisible, setIsVisible] = useState(false);
//   const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

//   useEffect(() => {
//     setIsVisible(true);
//   }, []);

//   const handleMouseMove = (e) => {
//     const rect = e.currentTarget.getBoundingClientRect();
//     const x = (e.clientX - rect.left) / rect.width;
//     const y = (e.clientY - rect.top) / rect.height;
//     setMousePosition({ x, y });
//   };

//   return (
//     <>
//       {/* Google Fonts Import - Futuristic Fonts */}
//       <link
//         href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Exo+2:wght@300;400;500;600;700;800&display=swap"
//         rel="stylesheet"
//       />
      
//     <div className="w-full min-h-[60vh] bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 overflow-hidden relative" style={{fontFamily: 'Orbitron, monospace'}}>
//       {/* Animated Background Elements */}
//       <div className="absolute inset-0 opacity-20">
//         <div className="absolute top-20 left-20 w-32 h-32 bg-purple-500 rounded-full blur-xl animate-pulse"></div>
//         <div className="absolute bottom-20 right-32 w-24 h-24 bg-blue-500 rounded-full blur-lg animate-bounce"></div>
//         <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-pink-500 rounded-full blur-md animate-ping"></div>
//       </div>

//       <div className="container mx-auto px-4 py-16 relative z-10 ml-96">
//         <div className="flex flex-col lg:flex-row items-start justify-start gap-12">
          
//           {/* Main Content - Positioned for left sidebar */}
//           <div className={`w-full space-y-6 transform transition-all duration-1000 ${
//             isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
//           }`}>
//             <div className="space-y-4">
//               <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
//                 <span className="inline-block animate-pulse bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent font-black tracking-wider">
//                   MANEESH MADHUSUDHANAN
//                 </span>
//                 <br />
//                 <span className="inline-block transform hover:scale-105 transition-transform duration-300 text-2xl md:text-4xl lg:text-5xl mt-2 text-cyan-300 font-semibold tracking-wide">
//                   BLOCKCHAIN DEVELOPER 
//                 </span>
//               </h1>
              
//               <p className="text-lg md:text-xl text-gray-300 max-w-3xl leading-relaxed animate-fade-in-up pr-8 font-light tracking-wide" style={{fontFamily: 'Exo 2, sans-serif'}}>
//                 Blockchain Developer skilled in Ethereum, Hyperledger Fabric, and Web3 application development, with experience in DAO governance, tokenomics, and decentralized exchanges. Proficient in Solidity, Hardhat, React.js, and backend integration, delivering secure, scalable blockchain solutions for finance, agriculture, IoT, and identity systems. Seeking to apply technical expertise and problem-solving skills to drive innovative decentralized projects in a collaborative environment.
//               </p>
//             </div>

//             <div className="flex flex-col sm:flex-row gap-4 pt-4">
//               <button className="px-8 py-4 bg-gradient-to-r from-cyan-500 via-purple-600 to-pink-600 text-white font-semibold rounded-full transform hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 animate-bounce text-sm md:text-base tracking-wider">
//                 DOWNLOAD RESUME
//               </button>
//               <button className="px-8 py-4 border-2 border-cyan-400 text-cyan-400 font-semibold rounded-full hover:bg-cyan-400 hover:text-gray-900 transition-all duration-300 hover:shadow-lg text-sm md:text-base tracking-wider">
//                 Learn More
//               </button>
//             </div>
//           </div>

//           {/* Image Section - Positioned for right side of content */}
//           <div className={`absolute top-16 right-8 lg:right-16 transform transition-all duration-1200 delay-300 ${
//             isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
//           }`}>
//             <div className="relative p-4 group">
              
//               {/* Animated Border Container */}
//               <div className="relative">
//                 {/* Rotating Border Animation */}
//                 <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-3xl blur-sm opacity-75 group-hover:opacity-100 animate-pulse"></div>
//                 <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-3xl animate-spin-slow opacity-50"></div>
                
//                 {/* Image Container - Medium Size */}
//                 <div 
//                   className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-2 transform group-hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-cyan-500/30"
//                   onMouseMove={handleMouseMove}
//                   style={{
//                     transform: `perspective(1000px) rotateX(${mousePosition.y * 5}deg) rotateY(${mousePosition.x * 5}deg)`
//                   }}
//                 >
//                   <div className="overflow-hidden rounded-2xl">
//                     <img
//                       src="/images/WhatsApp Image 2025-08-11 at 11.58.48 AM.jpeg"
//                       alt="Hero"
//                       className="w-full h-auto max-w-[200px] lg:max-w-[220px] object-cover transform group-hover:scale-110 transition-transform duration-700 filter group-hover:brightness-110 group-hover:contrast-110"
//                       loading="eager"
//                       decoding="async"
//                     />
                    
//                     {/* Overlay Effects */}
//                     <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
//                     <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 skew-x-12"></div>
//                   </div>
                  
//                   {/* Floating Elements */}
//                   <div className="absolute -top-3 -left-3 w-6 h-6 bg-cyan-500 rounded-full animate-bounce opacity-80"></div>
//                   <div className="absolute -bottom-3 -right-3 w-5 h-5 bg-pink-500 rounded-full animate-pulse opacity-80"></div>
//                   <div className="absolute top-1/2 -left-5 w-3 h-3 bg-purple-500 rounded-full animate-ping opacity-60"></div>
//                 </div>
//               </div>
//             </div>
//           </div>
          
//           {/* Left Sidebar Space - Clean space for your sidebar (approximately 15cm) */}
//           <div className="hidden lg:block fixed left-0 top-0 w-96 h-full bg-gradient-to-r from-slate-900/30 to-transparent pointer-events-none z-0">
//           </div>
//         </div>
//       </div>

//       {/* Custom CSS for additional animations */}
//       <style jsx>{`
//         @keyframes fade-in-up {
//           from {
//             opacity: 0;
//             transform: translateY(30px);
//           }
//           to {
//             opacity: 1;
//             transform: translateY(0);
//           }
//         }
        
//         @keyframes spin-slow {
//           from {
//             transform: rotate(0deg);
//           }
//           to {
//             transform: rotate(360deg);
//           }
//         }
        
//         .animate-fade-in-up {
//           animation: fade-in-up 1s ease-out 0.5s both;
//         }
        
//         .animate-spin-slow {
//           animation: spin-slow 8s linear infinite;
//         }
//       `}</style>
//     </div>
//     </>
//   );
// }





// import React, { useState, useEffect } from "react";

// export default function Hero3D() {
//   const [isVisible, setIsVisible] = useState(false);
//   const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

//   useEffect(() => {
//     setIsVisible(true);
//   }, []);

//   const handleMouseMove = (e) => {
//     const rect = e.currentTarget.getBoundingClientRect();
//     const x = (e.clientX - rect.left) / rect.width;
//     const y = (e.clientY - rect.top) / rect.height;
//     setMousePosition({ x, y });
//   };

//   return (
//     <>
//       {/* Google Fonts Import - Futuristic Fonts */}
//       <link
//         href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Exo+2:wght@300;400;500;600;700;800&display=swap"
//         rel="stylesheet"
//       />
      
//     <div className="w-full min-h-[60vh] bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 overflow-hidden relative" style={{fontFamily: 'Orbitron, monospace'}}>
//       {/* Animated Background Elements */}
//       <div className="absolute inset-0 opacity-20">
//         <div className="absolute top-20 left-20 w-32 h-32 bg-purple-500 rounded-full blur-xl animate-pulse"></div>
//         <div className="absolute bottom-20 right-32 w-24 h-24 bg-blue-500 rounded-full blur-lg animate-bounce"></div>
//         <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-pink-500 rounded-full blur-md animate-ping"></div>
//       </div>

//       <div className="container mx-auto px-4 py-16 relative z-10 ml-96">
//         <div className="flex flex-col lg:flex-row items-start justify-start gap-12">
          
//           {/* Main Content - Positioned for left sidebar */}
//           <div className={`w-full space-y-6 transform transition-all duration-1000 ${
//             isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
//           }`}>
//             <div className="space-y-4">
//               <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
//                 <span className="inline-block animate-pulse bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent font-black tracking-wider">
//                   MANEESH MADHUSUDHANAN
//                 </span>
//                 <br />
//                 <span className="inline-block transform hover:scale-105 transition-transform duration-300 text-2xl md:text-4xl lg:text-5xl mt-2 text-cyan-300 font-semibold tracking-wide">
//                   BLOCKCHAIN DEVELOPER 
//                 </span>
//               </h1>
              
//               <p className="text-lg md:text-xl text-gray-300 max-w-3xl leading-relaxed animate-fade-in-up pr-8 font-light tracking-wide" style={{fontFamily: 'Exo 2, sans-serif'}}>
//                 Blockchain Developer skilled in Ethereum, Hyperledger Fabric, and Web3 application development, with experience in DAO governance, tokenomics, and decentralized exchanges. Proficient in Solidity, Hardhat, React.js, and backend integration, delivering secure, scalable blockchain solutions for finance, agriculture, IoT, and identity systems. Seeking to apply technical expertise and problem-solving skills to drive innovative decentralized projects in a collaborative environment.
//               </p>
//             </div>

//             <div className="flex flex-col sm:flex-row gap-4 pt-4">
//               <button className="px-8 py-4 bg-gradient-to-r from-cyan-500 via-purple-600 to-pink-600 text-white font-semibold rounded-full transform hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 animate-bounce text-sm md:text-base tracking-wider">
//                 DOWNLOAD RESUME
//               </button>
//               <button className="px-8 py-4 border-2 border-cyan-400 text-cyan-400 font-semibold rounded-full hover:bg-cyan-400 hover:text-gray-900 transition-all duration-300 hover:shadow-lg text-sm md:text-base tracking-wider">
//                 Learn More
//               </button>
//             </div>
//           </div>

//           {/* Image Section - Positioned for right side of content */}
//           <div className={`absolute top-16 right-8 lg:right-16 transform transition-all duration-1200 delay-300 ${
//             isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
//           }`}>
//             <div className="relative p-4 group">
              
//               {/* Animated Border Container */}
//               <div className="relative">
//                 {/* Rotating Border Animation */}
//                 <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-3xl blur-sm opacity-75 group-hover:opacity-100 animate-pulse"></div>
//                 <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-3xl animate-spin-slow opacity-50"></div>
                
//                 {/* Image Container - Medium Size */}
//                 <div 
//                   className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-2 transform group-hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-cyan-500/30"
//                   onMouseMove={handleMouseMove}
//                   style={{
//                     transform: `perspective(1000px) rotateX(${mousePosition.y * 5}deg) rotateY(${mousePosition.x * 5}deg)`
//                   }}
//                 >
//                   <div className="overflow-hidden rounded-2xl">
//                     <img
//                       src="/images/WhatsApp Image 2025-08-11 at 11.58.48 AM.jpeg"
//                       alt="Hero"
//                       className="w-full h-auto max-w-[200px] lg:max-w-[220px] object-cover transform group-hover:scale-110 transition-transform duration-700 filter group-hover:brightness-110 group-hover:contrast-110"
//                       loading="eager"
//                       decoding="async"
//                     />
                    
//                     {/* Overlay Effects */}
//                     <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
//                     <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 skew-x-12"></div>
//                   </div>
                  
//                   {/* Floating Elements */}
//                   <div className="absolute -top-3 -left-3 w-6 h-6 bg-cyan-500 rounded-full animate-bounce opacity-80"></div>
//                   <div className="absolute -bottom-3 -right-3 w-5 h-5 bg-pink-500 rounded-full animate-pulse opacity-80"></div>
//                   <div className="absolute top-1/2 -left-5 w-3 h-3 bg-purple-500 rounded-full animate-ping opacity-60"></div>
//                 </div>
//               </div>
//             </div>
//           </div>
          
//           {/* Left Sidebar Space - Clean space for your sidebar (approximately 15cm) */}
//           <div className="hidden lg:block fixed left-0 top-0 w-96 h-full bg-gradient-to-r from-slate-900/30 to-transparent pointer-events-none z-0">
//           </div>
//         </div>
//       </div>

//       {/* Custom CSS for additional animations */}
//       <style jsx>{`
//         @keyframes fade-in-up {
//           from {
//             opacity: 0;
//             transform: translateY(30px);
//           }
//           to {
//             opacity: 1;
//             transform: translateY(0);
//           }
//         }
        
//         @keyframes spin-slow {
//           from {
//             transform: rotate(0deg);
//           }
//           to {
//             transform: rotate(360deg);
//           }
//         }
        
//         .animate-fade-in-up {
//           animation: fade-in-up 1s ease-out 0.5s both;
//         }
        
//         .animate-spin-slow {
//           animation: spin-slow 8s linear infinite;
//         }
//       `}</style>
//     </div>
//     </>
//   );
// }





// import React, { useState, useEffect } from "react";

// export default function Hero3D() {
//   const [isVisible, setIsVisible] = useState(false);
//   const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

//   useEffect(() => {
//     setIsVisible(true);
//   }, []);

//   const handleMouseMove = (e) => {
//     const rect = e.currentTarget.getBoundingClientRect();
//     const x = (e.clientX - rect.left) / rect.width;
//     const y = (e.clientY - rect.top) / rect.height;
//     setMousePosition({ x, y });
//   };

//   return (
//     <>
//       {/* Google Fonts Import - Futuristic Fonts */}
//       <link
//         href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Exo+2:wght@300;400;500;600;700;800&display=swap"
//         rel="stylesheet"
//       />
      
//     <div className="w-full min-h-[60vh] bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 overflow-hidden relative" style={{fontFamily: 'Orbitron, monospace'}}>
//       {/* Animated Background Elements */}
//       <div className="absolute inset-0 opacity-20">
//         <div className="absolute top-20 left-20 w-32 h-32 bg-purple-500 rounded-full blur-xl animate-pulse"></div>
//         <div className="absolute bottom-20 right-32 w-24 h-24 bg-blue-500 rounded-full blur-lg animate-bounce"></div>
//         <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-pink-500 rounded-full blur-md animate-ping"></div>
//       </div>

//       <div className="container mx-auto px-4 py-16 relative z-10 ml-80">
//         <div className="flex flex-col lg:flex-row items-start justify-start gap-12">
          
//           {/* Main Content - Positioned for left sidebar */}
//           <div className={`w-full space-y-6 transform transition-all duration-1000 ${
//             isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
//           }`}>
//             <div className="space-y-4">
//               <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
//                 <span className="inline-block animate-pulse bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
//                   Welcome
//                 </span>
//                 <br />
//                 <span className="inline-block transform hover:scale-105 transition-transform duration-300">
//                   to the Future
//                 </span>
//               </h1>
              
//               <p className="text-xl text-gray-300 max-w-lg leading-relaxed animate-fade-in-up">
//                 Experience cutting-edge design with stunning animations and interactive elements that bring your vision to life.
//               </p>
//             </div>

//             <div className="flex gap-4 pt-4">
//               <button className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-full transform hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 animate-bounce">
//                 Get Started
//               </button>
//               <button className="px-8 py-4 border-2 border-purple-400 text-purple-400 font-semibold rounded-full hover:bg-purple-400 hover:text-white transition-all duration-300 hover:shadow-lg">
//                 Learn More
//               </button>
//             </div>
//           </div>

//           {/* Image Section - Positioned for main content area */}
//           <div className={`absolute top-16 right-8 lg:right-16 transform transition-all duration-1200 delay-300 ${
//             isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
//           }`}>
//             <div className="relative p-4 group">
              
//               {/* Animated Border Container */}
//               <div className="relative">
//                 {/* Rotating Border Animation */}
//                 <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-3xl blur-sm opacity-75 group-hover:opacity-100 animate-pulse"></div>
//                 <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-3xl animate-spin-slow opacity-50"></div>
                
//                 {/* Image Container - Medium Size */}
//                 <div 
//                   className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-2 transform group-hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-cyan-500/30"
//                   onMouseMove={handleMouseMove}
//                   style={{
//                     transform: `perspective(1000px) rotateX(${mousePosition.y * 5}deg) rotateY(${mousePosition.x * 5}deg)`
//                   }}
//                 >
//                   <div className="overflow-hidden rounded-2xl">
//                     <img
//                       src="/images/WhatsApp Image 2025-08-11 at 11.58.48 AM.jpeg"
//                       alt="Hero"
//                       className="w-full h-auto max-w-[200px] lg:max-w-[220px] object-cover transform group-hover:scale-110 transition-transform duration-700 filter group-hover:brightness-110 group-hover:contrast-110"
//                       loading="eager"
//                       decoding="async"
//                     />
                    
//                     {/* Overlay Effects */}
//                     <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
//                     <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 skew-x-12"></div>
//                   </div>
                  
//                   {/* Floating Elements */}
//                   <div className="absolute -top-3 -left-3 w-6 h-6 bg-cyan-500 rounded-full animate-bounce opacity-80"></div>
//                   <div className="absolute -bottom-3 -right-3 w-5 h-5 bg-pink-500 rounded-full animate-pulse opacity-80"></div>
//                   <div className="absolute top-1/2 -left-5 w-3 h-3 bg-purple-500 rounded-full animate-ping opacity-60"></div>
//                 </div>
//               </div>
//             </div>
//           </div>
          
//           {/* Left Sidebar Space - Clean space for your sidebar */}
//           <div className="hidden lg:block fixed left-0 top-0 w-80 h-full bg-gradient-to-r from-slate-900/30 to-transparent pointer-events-none z-0">
//           </div>
//         </div>
//       </div>

//       {/* Custom CSS for additional animations */}
//       <style jsx>{`
//         @keyframes fade-in-up {
//           from {
//             opacity: 0;
//             transform: translateY(30px);
//           }
//           to {
//             opacity: 1;
//             transform: translateY(0);
//           }
//         }
        
//         @keyframes spin-slow {
//           from {
//             transform: rotate(0deg);
//           }
//           to {
//             transform: rotate(360deg);
//           }
//         }
        
//         .animate-fade-in-up {
//           animation: fade-in-up 1s ease-out 0.5s both;
//         }
        
//         .animate-spin-slow {
//           animation: spin-slow 8s linear infinite;
//         }
//       `}</style>
//     </div>
//     </>
//   );
// }







// import React, { useState, useEffect } from "react";

// export default function Hero3D() {
//   const [isVisible, setIsVisible] = useState(false);
//   const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

//   useEffect(() => {
//     setIsVisible(true);
//   }, []);

//   const handleMouseMove = (e) => {
//     const rect = e.currentTarget.getBoundingClientRect();
//     const x = (e.clientX - rect.left) / rect.width;
//     const y = (e.clientY - rect.top) / rect.height;
//     setMousePosition({ x, y });
//   };

//   return (
//     <>
//       {/* Google Fonts Import - Futuristic Fonts */}
//       <link
//         href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Exo+2:wght@300;400;500;600;700;800&display=swap"
//         rel="stylesheet"
//       />
      
//     <div className="w-full min-h-[60vh] bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 overflow-hidden relative" style={{fontFamily: 'Orbitron, monospace'}}>
//       {/* Animated Background Elements */}
//       <div className="absolute inset-0 opacity-20">
//         <div className="absolute top-20 left-20 w-32 h-32 bg-purple-500 rounded-full blur-xl animate-pulse"></div>
//         <div className="absolute bottom-20 right-32 w-24 h-24 bg-blue-500 rounded-full blur-lg animate-bounce"></div>
//         <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-pink-500 rounded-full blur-md animate-ping"></div>
//       </div>

//       <div className="container mx-auto px-4 py-16 relative z-10">
//         <div className="flex flex-col lg:flex-row items-start justify-start gap-12">
          
//           {/* Left Content - Moved back to left for right sidebar space */}
//           <div className={`w-full lg:w-2/3 xl:w-3/5 space-y-6 transform transition-all duration-1000 ${
//             isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
//           }`}>
//             <div className="space-y-4">
//               <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
//                 <span className="inline-block animate-pulse bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
//                   Welcome
//                 </span>
//                 <br />
//                 <span className="inline-block transform hover:scale-105 transition-transform duration-300">
//                   to the Future
//                 </span>
//               </h1>
              
//               <p className="text-xl text-gray-300 max-w-lg leading-relaxed animate-fade-in-up">
//                 Experience cutting-edge design with stunning animations and interactive elements that bring your vision to life.
//               </p>
//             </div>

//             <div className="flex gap-4 pt-4">
//               <button className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-full transform hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 animate-bounce">
//                 Get Started
//               </button>
//               <button className="px-8 py-4 border-2 border-purple-400 text-purple-400 font-semibold rounded-full hover:bg-purple-400 hover:text-white transition-all duration-300 hover:shadow-lg">
//                 Learn More
//               </button>
//             </div>
//           </div>

//           {/* Image Section - Medium size and positioned for left-aligned content */}
//           <div className={`absolute top-16 right-8 lg:right-16 xl:right-20 transform transition-all duration-1200 delay-300 ${
//             isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
//           }`}>
//             <div className="relative p-4 group">
              
//               {/* Animated Border Container */}
//               <div className="relative">
//                 {/* Rotating Border Animation */}
//                 <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-3xl blur-sm opacity-75 group-hover:opacity-100 animate-pulse"></div>
//                 <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-3xl animate-spin-slow opacity-50"></div>
                
//                 {/* Image Container - Medium Size */}
//                 <div 
//                   className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-2 transform group-hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-cyan-500/30"
//                   onMouseMove={handleMouseMove}
//                   style={{
//                     transform: `perspective(1000px) rotateX(${mousePosition.y * 5}deg) rotateY(${mousePosition.x * 5}deg)`
//                   }}
//                 >
//                   <div className="overflow-hidden rounded-2xl">
//                     <img
//                       src="/images/WhatsApp Image 2025-08-11 at 11.58.48 AM.jpeg"
//                       alt="Hero"
//                       className="w-full h-auto max-w-[200px] lg:max-w-[220px] object-cover transform group-hover:scale-110 transition-transform duration-700 filter group-hover:brightness-110 group-hover:contrast-110"
//                       loading="eager"
//                       decoding="async"
//                     />
                    
//                     {/* Overlay Effects */}
//                     <div className="absolute inset-0 bg-gradient-to-t from-cyan-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
//                     <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 skew-x-12"></div>
//                   </div>
                  
//                   {/* Floating Elements */}
//                   <div className="absolute -top-3 -left-3 w-6 h-6 bg-cyan-500 rounded-full animate-bounce opacity-80"></div>
//                   <div className="absolute -bottom-3 -right-3 w-5 h-5 bg-pink-500 rounded-full animate-pulse opacity-80"></div>
//                   <div className="absolute top-1/2 -left-5 w-3 h-3 bg-purple-500 rounded-full animate-ping opacity-60"></div>
//                 </div>
//               </div>
//             </div>
//           </div>
          
//           {/* Right Sidebar Space Indicator - Shows where your sidebar will be */}
//           <div className="hidden lg:block fixed right-0 top-0 w-80 h-full bg-gradient-to-l from-cyan-900/20 to-transparent pointer-events-none z-0">
//             <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-cyan-400/50 text-sm rotate-90 whitespace-nowrap">
//               RIGHT SIDEBAR SPACE
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Custom CSS for additional animations */}
//       <style jsx>{`
//         @keyframes fade-in-up {
//           from {
//             opacity: 0;
//             transform: translateY(30px);
//           }
//           to {
//             opacity: 1;
//             transform: translateY(0);
//           }
//         }
        
//         @keyframes spin-slow {
//           from {
//             transform: rotate(0deg);
//           }
//           to {
//             transform: rotate(360deg);
//           }
//         }
        
//         .animate-fade-in-up {
//           animation: fade-in-up 1s ease-out 0.5s both;
//         }
        
//         .animate-spin-slow {
//           animation: spin-slow 8s linear infinite;
//         }
//       `}</style>
//     </div>
//     </>
//   );
// }


// import React, { useState, useEffect } from "react";

// export default function Hero3D() {
//   const [isVisible, setIsVisible] = useState(false);
//   const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

//   useEffect(() => {
//     setIsVisible(true);
//   }, []);

//   const handleMouseMove = (e) => {
//     const rect = e.currentTarget.getBoundingClientRect();
//     const x = (e.clientX - rect.left) / rect.width;
//     const y = (e.clientY - rect.top) / rect.height;
//     setMousePosition({ x, y });
//   };

//   return (
//     <>
//       {/* Google Fonts Import */}
//       <link
//         href="https://fonts.googleapis.com/css2?family=Oxanium:wght@200;300;400;500;600;700;800&display=swap"
//         rel="stylesheet"
//       />
      
//     <div className="w-full min-h-[60vh] bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 overflow-hidden relative" style={{fontFamily: 'Oxanium, sans-serif'}}>
//       {/* Animated Background Elements */}
//       <div className="absolute inset-0 opacity-20">
//         <div className="absolute top-20 left-20 w-32 h-32 bg-purple-500 rounded-full blur-xl animate-pulse"></div>
//         <div className="absolute bottom-20 right-32 w-24 h-24 bg-blue-500 rounded-full blur-lg animate-bounce"></div>
//         <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-pink-500 rounded-full blur-md animate-ping"></div>
//       </div>

//       <div className="container mx-auto px-4 py-16 relative z-10">
//         <div className="flex flex-col lg:flex-row items-start justify-end gap-12">
          
//           {/* Right Content - Moved to right for left sidebar space */}
//           <div className={`w-full lg:w-2/3 xl:w-3/5 lg:ml-auto space-y-6 transform transition-all duration-1000 ${
//             isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
//           }`}>
//             <div className="space-y-4">
//               <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
//                 <span className="inline-block animate-pulse bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
//                   Welcome
//                 </span>
//                 <br />
//                 <span className="inline-block transform hover:scale-105 transition-transform duration-300">
//                   to the Future
//                 </span>
//               </h1>
              
//               <p className="text-xl text-gray-300 max-w-lg leading-relaxed animate-fade-in-up">
//                 Experience cutting-edge design with stunning animations and interactive elements that bring your vision to life.
//               </p>
//             </div>

//             <div className="flex gap-4 pt-4">
//               <button className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-full transform hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 animate-bounce">
//                 Get Started
//               </button>
//               <button className="px-8 py-4 border-2 border-purple-400 text-purple-400 font-semibold rounded-full hover:bg-purple-400 hover:text-white transition-all duration-300 hover:shadow-lg">
//                 Learn More
//               </button>
//             </div>
//           </div>

//           {/* Image Section - Positioned for right side layout */}
//           <div className={`absolute top-16 right-8 lg:right-16 xl:right-20 transform transition-all duration-1200 delay-300 ${
//             isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
//           }`}>
//             <div className="relative p-6 group">
              
//               {/* Animated Border Container */}
//               <div className="relative">
//                 {/* Rotating Border Animation */}
//                 <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-3xl blur-sm opacity-75 group-hover:opacity-100 animate-pulse"></div>
//                 <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-3xl animate-spin-slow opacity-50"></div>
                
//                 {/* Image Container */}
//                 <div 
//                   className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-3 transform group-hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/30"
//                   onMouseMove={handleMouseMove}
//                   style={{
//                     transform: `perspective(1000px) rotateX(${mousePosition.y * 5}deg) rotateY(${mousePosition.x * 5}deg)`
//                   }}
//                 >
//                   <div className="overflow-hidden rounded-2xl">
//                     <img
//                       src="/images/WhatsApp Image 2025-08-11 at 11.58.48 AM.jpeg"
//                       alt="Hero"
//                       className="w-full h-auto max-w-[250px] lg:max-w-[300px] object-cover transform group-hover:scale-110 transition-transform duration-700 filter group-hover:brightness-110 group-hover:contrast-110"
//                       loading="eager"
//                       decoding="async"
//                     />
                    
//                     {/* Overlay Effects */}
//                     <div className="absolute inset-0 bg-gradient-to-t from-purple-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
//                     <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 skew-x-12"></div>
//                   </div>
                  
//                   {/* Floating Elements */}
//                   <div className="absolute -top-4 -left-4 w-8 h-8 bg-purple-500 rounded-full animate-bounce opacity-80"></div>
//                   <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-pink-500 rounded-full animate-pulse opacity-80"></div>
//                   <div className="absolute top-1/2 -left-6 w-4 h-4 bg-blue-500 rounded-full animate-ping opacity-60"></div>
//                 </div>
//               </div>
//             </div>
//           </div>
          
//           {/* Left Sidebar Space Indicator - Shows where your sidebar will be */}
//           <div className="hidden lg:block fixed left-0 top-0 w-80 h-full bg-gradient-to-r from-purple-900/20 to-transparent pointer-events-none z-0">
//             <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-purple-400/50 text-sm -rotate-90 whitespace-nowrap">
//               LEFT SIDEBAR SPACE
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Custom CSS for additional animations */}
//       <style jsx>{`
//         @keyframes fade-in-up {
//           from {
//             opacity: 0;
//             transform: translateY(30px);
//           }
//           to {
//             opacity: 1;
//             transform: translateY(0);
//           }
//         }
        
//         @keyframes spin-slow {
//           from {
//             transform: rotate(0deg);
//           }
//           to {
//             transform: rotate(360deg);
//           }
//         }
        
//         .animate-fade-in-up {
//           animation: fade-in-up 1s ease-out 0.5s both;
//         }
        
//         .animate-spin-slow {
//           animation: spin-slow 8s linear infinite;
//         }
//       `}</style>
//     </div>
//     </>
//   );
// }

// import React, { useState, useEffect } from "react";

// export default function Hero3D() {
//   const [isVisible, setIsVisible] = useState(false);
//   const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

//   useEffect(() => {
//     setIsVisible(true);
//   }, []);

//   const handleMouseMove = (e) => {
//     const rect = e.currentTarget.getBoundingClientRect();
//     const x = (e.clientX - rect.left) / rect.width;
//     const y = (e.clientY - rect.top) / rect.height;
//     setMousePosition({ x, y });
//   };

//   return (
//     <>
//       {/* Google Fonts Import */}
//       <link
//         href="https://fonts.googleapis.com/css2?family=Oxanium:wght@200;300;400;500;600;700;800&display=swap"
//         rel="stylesheet"
//       />
      
//     <div className="w-full min-h-[60vh] bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 overflow-hidden relative" style={{fontFamily: 'Oxanium, sans-serif'}}>
//       {/* Animated Background Elements */}
//       <div className="absolute inset-0 opacity-20">
//         <div className="absolute top-20 left-20 w-32 h-32 bg-purple-500 rounded-full blur-xl animate-pulse"></div>
//         <div className="absolute bottom-20 right-32 w-24 h-24 bg-blue-500 rounded-full blur-lg animate-bounce"></div>
//         <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-pink-500 rounded-full blur-md animate-ping"></div>
//       </div>

//       <div className="container mx-auto px-4 py-16 relative z-10">
//         <div className="flex flex-col lg:flex-row items-start justify-start gap-12">
          
//           {/* Left Content - Adjusted for sidebar space */}
//           <div className={`w-full lg:w-2/3 xl:w-3/5 space-y-6 transform transition-all duration-1000 ${
//             isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
//           }`}>
//             <div className="space-y-4">
//               <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
//                 <span className="inline-block animate-pulse bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
//                   Welcome
//                 </span>
//                 <br />
//                 <span className="inline-block transform hover:scale-105 transition-transform duration-300">
//                   to the Future
//                 </span>
//               </h1>
              
//               <p className="text-xl text-gray-300 max-w-lg leading-relaxed animate-fade-in-up">
//                 Experience cutting-edge design with stunning animations and interactive elements that bring your vision to life.
//               </p>
//             </div>

//             <div className="flex gap-4 pt-4">
//               <button className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-full transform hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 animate-bounce">
//                 Get Started
//               </button>
//               <button className="px-8 py-4 border-2 border-purple-400 text-purple-400 font-semibold rounded-full hover:bg-purple-400 hover:text-white transition-all duration-300 hover:shadow-lg">
//                 Learn More
//               </button>
//             </div>
//           </div>

//           {/* Right Image Section - Positioned absolutely for sidebar space */}
//           <div className={`absolute top-16 right-8 lg:right-16 transform transition-all duration-1200 delay-300 ${
//             isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
//           }`}>
//             <div className="relative p-6 group">
              
//               {/* Animated Border Container */}
//               <div className="relative">
//                 {/* Rotating Border Animation */}
//                 <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-3xl blur-sm opacity-75 group-hover:opacity-100 animate-pulse"></div>
//                 <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-3xl animate-spin-slow opacity-50"></div>
                
//                 {/* Image Container */}
//                 <div 
//                   className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-3 transform group-hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/30"
//                   onMouseMove={handleMouseMove}
//                   style={{
//                     transform: `perspective(1000px) rotateX(${mousePosition.y * 5}deg) rotateY(${mousePosition.x * 5}deg)`
//                   }}
//                 >
//                   <div className="overflow-hidden rounded-2xl">
//                     <img
//                       src="/images/WhatsApp Image 2025-08-11 at 11.58.48 AM.jpeg"
//                       alt="Hero"
//                       className="w-full h-auto max-w-[250px] lg:max-w-[300px] object-cover transform group-hover:scale-110 transition-transform duration-700 filter group-hover:brightness-110 group-hover:contrast-110"
//                       loading="eager"
//                       decoding="async"
//                     />
                    
//                     {/* Overlay Effects */}
//                     <div className="absolute inset-0 bg-gradient-to-t from-purple-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
//                     <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 skew-x-12"></div>
//                   </div>
                  
//                   {/* Floating Elements */}
//                   <div className="absolute -top-4 -left-4 w-8 h-8 bg-purple-500 rounded-full animate-bounce opacity-80"></div>
//                   <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-pink-500 rounded-full animate-pulse opacity-80"></div>
//                   <div className="absolute top-1/2 -left-6 w-4 h-4 bg-blue-500 rounded-full animate-ping opacity-60"></div>
//                 </div>
//               </div>
//             </div>
//           </div>
          
//           {/* Sidebar Space Indicator - This shows where your sidebar will be */}
//           <div className="hidden lg:block fixed right-0 top-0 w-80 h-full bg-gradient-to-l from-purple-900/20 to-transparent pointer-events-none z-0">
//             <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-purple-400/50 text-sm rotate-90 whitespace-nowrap">
//               SIDEBAR SPACE
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Custom CSS for additional animations */}
//       <style jsx>{`
//         @keyframes fade-in-up {
//           from {
//             opacity: 0;
//             transform: translateY(30px);
//           }
//           to {
//             opacity: 1;
//             transform: translateY(0);
//           }
//         }
        
//         @keyframes spin-slow {
//           from {
//             transform: rotate(0deg);
//           }
//           to {
//             transform: rotate(360deg);
//           }
//         }
        
//         .animate-fade-in-up {
//           animation: fade-in-up 1s ease-out 0.5s both;
//         }
        
//         .animate-spin-slow {
//           animation: spin-slow 8s linear infinite;
//         }
//       `}</style>
//     </div>
//     </>
//   );
// }

///////////////////////////////////////////////////////////////////////////////

// import React, { useState, useEffect } from "react";

// export default function Hero3D() {
//   const [isVisible, setIsVisible] = useState(false);
//   const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

//   useEffect(() => {
//     setIsVisible(true);
//   }, []);

//   const handleMouseMove = (e) => {
//     const rect = e.currentTarget.getBoundingClientRect();
//     const x = (e.clientX - rect.left) / rect.width;
//     const y = (e.clientY - rect.top) / rect.height;
//     setMousePosition({ x, y });
//   };

//   return (
//     <div className="w-full min-h-[60vh] bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 overflow-hidden relative">
//       {/* Animated Background Elements */}
//       <div className="absolute inset-0 opacity-20">
//         <div className="absolute top-20 left-20 w-32 h-32 bg-purple-500 rounded-full blur-xl animate-pulse"></div>
//         <div className="absolute bottom-20 right-32 w-24 h-24 bg-blue-500 rounded-full blur-lg animate-bounce"></div>
//         <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-pink-500 rounded-full blur-md animate-ping"></div>
//       </div>

//       <div className="container mx-auto px-4 py-16 relative z-10">
//         <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
          
//           {/* Left Content */}
//           <div className={`flex-1 space-y-6 transform transition-all duration-1000 ${
//             isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
//           }`}>
//             <div className="space-y-4">
//               <h1 className="text-2xl md:text-4xl font-bold text-white leading-tight">
//                 <span className="inline-block animate-pulse bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
//                   MANEESH MADHUSUDHANAN
//                 </span>
//                 <br />
//                 <span className="inline-block transform hover:scale-105 transition-transform duration-300">
//                   BLOCKCHAIN DEVELOPER 
//                 </span>
//               </h1>
              
//               <p className="text-xl text-gray-300 max-w-lg leading-relaxed animate-fade-in-up">
//               Blockchain Developer skilled in Ethereum, Hyperledger Fabric, and Web3 application development, with experience in DAO governance, tokenomics, and decentralized exchanges. Proficient in Solidity, Hardhat, React.js, and backend integration, delivering secure, scalable blockchain solutions for finance, agriculture, IoT, and identity systems. Seeking to apply technical expertise and problem-solving skills to drive innovative decentralized projects in a collaborative environment.              </p>
//             </div>

//             <div className="flex gap-4 pt-4">
//               <button className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-full transform hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 animate-bounce">
//                 DOWNLOAD RESUME
//               </button>
//               <button className="px-8 py-4 border-2 border-purple-400 text-purple-400 font-semibold rounded-full hover:bg-purple-400 hover:text-white transition-all duration-300 hover:shadow-lg">
//                 Learn More
//               </button>
//             </div>
//           </div>

//           {/* Right Image Section */}
//           <div className={`flex-1 flex justify-end transform transition-all duration-1200 delay-300 ${
//             isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
//           }`}>
//             <div className="relative p-6 group">
              
//               {/* Animated Border Container */}
//               <div className="relative">
//                 {/* Rotating Border Animation */}
//                 <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-3xl blur-sm opacity-75 group-hover:opacity-100 animate-pulse"></div>
//                 <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-3xl animate-spin-slow opacity-50"></div>
                
//                 {/* Image Container */}
//                 <div 
//                   className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-3 transform group-hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/30"
//                   onMouseMove={handleMouseMove}
//                   style={{
//                     transform: `perspective(1000px) rotateX(${mousePosition.y * 5}deg) rotateY(${mousePosition.x * 5}deg)`
//                   }}
//                 >
//                   <div className="overflow-hidden rounded-2xl">
//                     <img
//                       src="/images/WhatsApp Image 2025-08-11 at 11.58.48 AM.jpeg"
//                       alt="Hero"
//                       className="w-full h-auto max-w-md object-cover transform group-hover:scale-110 transition-transform duration-700 filter group-hover:brightness-110 group-hover:contrast-110"
//                       loading="eager"
//                       decoding="async"
//                     />
                    
//                     {/* Overlay Effects */}
//                     <div className="absolute inset-0 bg-gradient-to-t from-purple-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
//                     <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 skew-x-12"></div>
//                   </div>
                  
//                   {/* Floating Elements */}
//                   <div className="absolute -top-4 -left-4 w-8 h-8 bg-purple-500 rounded-full animate-bounce opacity-80"></div>
//                   <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-pink-500 rounded-full animate-pulse opacity-80"></div>
//                   <div className="absolute top-1/2 -left-6 w-4 h-4 bg-blue-500 rounded-full animate-ping opacity-60"></div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Custom CSS for additional animations */}
//       <style jsx>{`
//         @keyframes fade-in-up {
//           from {
//             opacity: 0;
//             transform: translateY(30px);
//           }
//           to {
//             opacity: 1;
//             transform: translateY(0);
//           }
//         }
        
//         @keyframes spin-slow {
//           from {
//             transform: rotate(0deg);
//           }
//           to {
//             transform: rotate(360deg);
//           }
//         }
        
//         .animate-fade-in-up {
//           animation: fade-in-up 1s ease-out 0.5s both;
//         }
        
//         .animate-spin-slow {
//           animation: spin-slow 8s linear infinite;
//         }
//       `}</style>
//     </div>
//   );
// }
// // import React from "react";

// // export default function Hero3D() {
// //   return (
// //     <div style={{ width: "100%", minHeight: "60vh" }}>
// //       <img
// //         src="/images/WhatsApp Image 2025-08-11 at 11.58.48 AM.jpeg"
// //         alt="Hero"
// //         style={{ width: "50%", height: "100%", objectFit: "cover", display: "block" }}
// //         loading="eager"
// //         decoding="async"
// //       />
// //     </div>
// //   );
// // }